import { Badge, message, Modal } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { AiOutlineFile } from 'react-icons/ai';
import { BsReceipt } from 'react-icons/bs';
import { HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi';
import { useHistory } from 'react-router-dom';

import { capitalizeFirstLetter } from '../../helpers/capitalizeFirstLetter';

import { DividasAlert } from '../../components/DividasAlert';
import { PanelLayout } from '../../components/PanelLayout';
import { FeedbackModal } from '../../components/PanelLayout/Modals/FeedbackModal';
import { ObligationModal } from '../../components/PanelLayout/Modals/ObligationModal';
import { SESSION_STORAGE } from '../../constants/session-storage.constants';
import { getFilteredFiles } from '../../helpers/getFilteredFiles';
import { useAuth } from '../../hooks/useAuth';
import { useMobileVersion } from '../../hooks/useMobileVersion';
import { useQuery } from '../../hooks/useQuery';
import { getDividasTributarias, DividaTributaria } from '../../operations/dividasTributarias';
import { getAllSolicitations } from '../../operations/solicitations';
import { getFilesByYearAndMonth } from '../../operations/file';
import { ObligationFile } from '../../operations/file/types';
import { Calendar, FileStatus } from './Calendar';
import { MobileFiles } from './MobileFiles';
import { MobileTimelineSkeleton } from './MobileSkeleton';
import {
  CalendarSectionHeader,
  CalendarSectionSubtitle,
  CalendarSectionTitle,
  Container,
  DownloadedButton,
  ExpiredButton,
  FilterButtonsContainer,
  Legend,
  MobileMonthButton,
  MobileMonthLabel,
  MobileMonthNav,
  MobileQuickAccessLabel,
  MobileQuickAction,
  MobileQuickActionBadge,
  MobileQuickActionContent,
  MobileQuickActionDescription,
  MobileQuickActionIcon,
  MobileQuickActionLabel,
  MobileQuickActions,
  MobileSectionTitle,
  NoDataContainer,
  NonDownloadedButton,
  QuickAccessArrow,
  QuickAccessBadge,
  QuickAccessCard,
  QuickAccessCards,
  QuickAccessContent,
  QuickAccessDescription,
  QuickAccessIcon,
  QuickAccessLabel,
  QuickAccessTitle,
  WelcomeSection,
  WelcomeSubtitle,
  WelcomeTitle,
} from './styles';

type FeedbackModalProps = {
  isVisible: boolean;
  file: ObligationFile | null;
};

export type FilesModalProps = {
  isVisible: boolean;
  files: ObligationFile[];
  status: FileStatus;
};

export const Dashboard = () => {
  const [obligationModal, setObligationModal] = useState({
    isVisible: false,
  });
  const [feedbackModal, setFeedbackModal] = useState<FeedbackModalProps>({
    isVisible: false,
    file: null,
  });
  const [filesModal, setFilesModal] = useState<FilesModalProps>({
    isVisible: false,
    files: [],
    status: 'expired',
  });
  const [modalData, setModalData] = useState<ObligationFile>(
    {} as ObligationFile,
  );
  const [files, setFiles] = useState<ObligationFile[]>([]);
  const [dividas, setDividas] = useState<DividaTributaria[]>([]);
  const [pendingSolicitations, setPendingSolicitations] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [filesStatus, setFilesStatus] = useState({
    isVisualized: true,
    isUnviewed: true,
    isExpired: true,
  });
  const { user } = useAuth();

  const isMobileVersion = useMobileVersion();
  const query = useQuery();
  const history = useHistory();

  const closeObligatinModal = () => {
    if (query.has('file')) {
      query.delete('file');
      history.replace({
        search: undefined,
      });
    }
    setObligationModal({
      isVisible: false,
    });
  };

  const closeFeedbackModal = () => {
    setFeedbackModal({
      isVisible: false,
      file: null,
    });
  };

  const openObligationModal = (file: ObligationFile | undefined) => {
    if (file) {
      setModalData(file);
      setObligationModal({
        isVisible: true,
      });
    }
  };

  const openFeedbackModal = (file: ObligationFile) => {
    setFeedbackModal({ isVisible: true, file });
  };

  const handleFileStatus = (status: keyof typeof filesStatus) => {
    setFilesStatus((statusInState) => ({
      ...statusInState,
      [status]: !statusInState[status],
    }));
  };

  const updateFiles = (updatedFile: ObligationFile) => {
    const { viewed_at, viewed_by } = updatedFile;
    setFiles(
      files.map((file) => {
        if (file.id !== updatedFile.id) {
          return file;
        }
        return {
          ...file,
          viewed_at,
          viewed_by,
        };
      }),
    );
    setFilesModal({
      ...filesModal,
      files: filesModal.files.filter(({ id, viewed_at: viewedAt }) => {
        if (id !== updatedFile.id) {
          return true;
        }
        if (!!updatedFile.viewed_at && !viewedAt) {
          return false;
        }
        return true;
      }),
    });
  };

  useEffect(() => {
    const isRegisterIncomplete = !!sessionStorage.getItem(
      SESSION_STORAGE.SHOW_INCOMPLETE_REGISTER_WARNING,
    );

    if (isRegisterIncomplete) {
      Modal.confirm({
        title:
          'Cadastro incompleto, por favor clique no botão abaixo para completar o seu cadastro.',
        okText: 'Ir para configurações',
        cancelText: 'Fechar',
        maskClosable: false,
        onCancel: () => {
          sessionStorage.removeItem(
            SESSION_STORAGE.SHOW_INCOMPLETE_REGISTER_WARNING,
          );
        },
        onOk: () => {
          history.push('/configuracoes');
        },
      });
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    getDividasTributarias()
      .then((data) => {
        if (mounted) {
          setDividas(data);
        }
      })
      .catch(() => {
        // Silently fail - dividas alert is not critical
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!user || !user.allow_nf) return;
    let mounted = true;
    getAllSolicitations()
      .then((data) => {
        if (mounted && Array.isArray(data)) {
          setPendingSolicitations(data.filter((s: any) => s.status === 0).length);
        }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [user && user.allow_nf]);

  useEffect(() => {
    if (!user!.id) return;
    let mounted = true;
    setIsLoading(true);
    getFilesByYearAndMonth(selectedDate.format('YYYY-MM'))
      .then((filesFromApi) => {
        if (mounted) {
          setFiles(filesFromApi);
        }
      })
      .finally(() => {
        if (mounted) {
          setIsLoading(false);
          setHasLoadedOnce(true);
        }
      });
    return () => {
      mounted = false;
    };
  }, [user, selectedDate]);

  useEffect(() => {
    if (query.has('file') && files.length > 0) {
      const selectedFile = files.find(
        (file) => file.id === Number(query.get('file')),
      );

      if (selectedFile) {
        openObligationModal(selectedFile);
      } else {
        message.warning(
          'O arquivo selecionado não existe ou não esta mais disponível.',
        );
      }
    }
  }, [files]);

  const { visualizedFiles, expiredFiles, unviewedFiles } = getFilteredFiles(files);

  const handlePreviousMonth = () => {
    setSelectedDate(moment(selectedDate).subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setSelectedDate(moment(selectedDate).add(1, 'month'));
  };

  const { isExpired, isVisualized, isUnviewed } = filesStatus;

  const filteredFiles = [
    isVisualized ? visualizedFiles : [],
    isExpired ? expiredFiles : [],
    isUnviewed ? unviewedFiles : [],
  ].flat();

  return (
    <PanelLayout>
      {isMobileVersion ? (
        <>
          <WelcomeSection>
            <WelcomeTitle>Olá, {user?.name?.split(' ')[0] || 'Cliente'}!</WelcomeTitle>
            <WelcomeSubtitle>Acompanhe suas obrigações</WelcomeSubtitle>
          </WelcomeSection>

          <MobileQuickAccessLabel>Acesso Rápido</MobileQuickAccessLabel>
          <MobileQuickActions>
            <MobileQuickAction onClick={() => history.push('/arquivos')}>
              <MobileQuickActionIcon>
                <AiOutlineFile />
              </MobileQuickActionIcon>
              <MobileQuickActionContent>
                <MobileQuickActionLabel>Documentos não baixados</MobileQuickActionLabel>
                <MobileQuickActionDescription>Pendentes para baixar</MobileQuickActionDescription>
              </MobileQuickActionContent>
              <MobileQuickActionBadge>
                <Badge showZero count={unviewedFiles.length} style={{ backgroundColor: '#f5222d' }} />
              </MobileQuickActionBadge>
            </MobileQuickAction>
            {user && user.allow_nf && (
              <MobileQuickAction onClick={() => history.push('/solicitacoes')}>
                <MobileQuickActionIcon style={{ background: '#e6f7ff' }}>
                  <BsReceipt style={{ color: '#1890ff' }} />
                </MobileQuickActionIcon>
                <MobileQuickActionContent>
                  <MobileQuickActionLabel>Notas Fiscais</MobileQuickActionLabel>
                  <MobileQuickActionDescription>
                    {pendingSolicitations > 0
                      ? pendingSolicitations + (pendingSolicitations === 1 ? ' pendente' : ' pendentes')
                      : 'Solicitar emissão de NF-e'}
                  </MobileQuickActionDescription>
                </MobileQuickActionContent>
                {pendingSolicitations > 0 && (
                  <MobileQuickActionBadge>
                    <Badge count={pendingSolicitations} style={{ backgroundColor: '#fa8c16' }} />
                  </MobileQuickActionBadge>
                )}
              </MobileQuickAction>
            )}
            <DividasAlert dividas={dividas} />
          </MobileQuickActions>

          <MobileSectionTitle>Obrigações do Mês</MobileSectionTitle>

          <MobileMonthNav>
            <MobileMonthButton onClick={handlePreviousMonth}>
              <HiOutlineChevronLeft />
            </MobileMonthButton>
            <MobileMonthLabel>
              {capitalizeFirstLetter(selectedDate.format('MMMM [de] YYYY'))}
            </MobileMonthLabel>
            <MobileMonthButton onClick={handleNextMonth}>
              <HiOutlineChevronRight />
            </MobileMonthButton>
          </MobileMonthNav>

          {isLoading ? (
            <MobileTimelineSkeleton />
          ) : files.length === 0 ? (
            <NoDataContainer>
              <p>Nenhuma obrigação encontrada para este mês.</p>
              <img src="/images/no-content-image.svg" alt="Sem dados" style={{ maxWidth: 200 }} />
            </NoDataContainer>
          ) : (
            <MobileFiles
              selectedDate={selectedDate}
              files={filteredFiles}
              openDrawer={openObligationModal}
            />
          )}
        </>
      ) : (
        <>
          <WelcomeSection>
            <WelcomeTitle>Bem-vindo ao painel do cliente</WelcomeTitle>
            <WelcomeSubtitle>Acesse suas informações e obrigações</WelcomeSubtitle>

            <QuickAccessTitle>Acesso Rápido</QuickAccessTitle>
            <QuickAccessCards>
              <QuickAccessCard onClick={() => history.push('/arquivos')}>
                <QuickAccessIcon>
                  <AiOutlineFile />
                </QuickAccessIcon>
                <QuickAccessContent>
                  <QuickAccessLabel>Documentos não baixados</QuickAccessLabel>
                  <QuickAccessDescription>Documentos pendentes para baixar</QuickAccessDescription>
                </QuickAccessContent>
                <QuickAccessBadge>
                  <Badge showZero count={unviewedFiles.length} style={{ backgroundColor: '#f5222d' }} />
                </QuickAccessBadge>
                <QuickAccessArrow>
                  <HiOutlineChevronRight />
                </QuickAccessArrow>
              </QuickAccessCard>
              {user && user.allow_nf && (
                <QuickAccessCard onClick={() => history.push('/solicitacoes')}>
                  <QuickAccessIcon style={{ background: '#e6f7ff' }}>
                    <BsReceipt style={{ color: '#1890ff' }} />
                  </QuickAccessIcon>
                  <QuickAccessContent>
                    <QuickAccessLabel>Notas Fiscais</QuickAccessLabel>
                    <QuickAccessDescription>
                      {pendingSolicitations > 0
                        ? pendingSolicitations + (pendingSolicitations === 1 ? ' solicitação pendente' : ' solicitações pendentes')
                        : 'Solicitar emissão de NF-e'}
                    </QuickAccessDescription>
                  </QuickAccessContent>
                  {pendingSolicitations > 0 && (
                    <QuickAccessBadge>
                      <Badge count={pendingSolicitations} style={{ backgroundColor: '#fa8c16' }} />
                    </QuickAccessBadge>
                  )}
                  <QuickAccessArrow>
                    <HiOutlineChevronRight />
                  </QuickAccessArrow>
                </QuickAccessCard>
              )}
              <DividasAlert dividas={dividas} />
            </QuickAccessCards>
          </WelcomeSection>

          <CalendarSectionHeader>
            <CalendarSectionTitle>Calendário de Obrigações</CalendarSectionTitle>
            <CalendarSectionSubtitle>Visualize suas obrigações fiscais e contábeis</CalendarSectionSubtitle>
          </CalendarSectionHeader>

          <Container>
            <Legend>
              <FilterButtonsContainer>
              <DownloadedButton
                $isActive={filesStatus.isVisualized}
                onClick={() => handleFileStatus('isVisualized')}
              >
                BAIXADOS
                <Badge
                  showZero
                  size="small"
                  count={visualizedFiles.length}
                  style={{ backgroundColor: 'var(--success)' }}
                />
              </DownloadedButton>
              <NonDownloadedButton
                $isActive={filesStatus.isUnviewed}
                onClick={() => handleFileStatus('isUnviewed')}
              >
                NÃO BAIXADOS
                <Badge
                  showZero
                  size="small"
                  count={unviewedFiles.length}
                  style={{ backgroundColor: 'var(--info)' }}
                />
              </NonDownloadedButton>
              <ExpiredButton
                $isActive={filesStatus.isExpired}
                onClick={() => handleFileStatus('isExpired')}
              >
                VENCIDOS
                <Badge
                  showZero
                  size="small"
                  count={expiredFiles.length}
                  style={{ backgroundColor: 'var(--danger)' }}
                />
              </ExpiredButton>
            </FilterButtonsContainer>
          </Legend>

          <Calendar
            files={filteredFiles}
            isLoading={isLoading}
            hasLoadedOnce={hasLoadedOnce}
            openModal={openObligationModal}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            filesModal={filesModal}
            setFilesModal={setFilesModal}
          />
          </Container>
        </>
      )}
      <ObligationModal
        data={modalData}
        isVisible={obligationModal.isVisible}
        onClose={closeObligatinModal}
        updateFiles={updateFiles}
        setModalData={setModalData}
        openFeedbackModal={openFeedbackModal}
      />
      <FeedbackModal
        title="Como foi sua experiência baixando este documento?"
        isVisible={feedbackModal.isVisible}
        file={feedbackModal.file}
        onClose={closeFeedbackModal}
      />
    </PanelLayout>
  );
};
